/* eslint-disable no-underscore-dangle */
import { PartnerBook, PartnerBookUpdatedPartner } from '../types/PartnerBook';

type BuildPartnerBookProps = {
  partnerBook: PartnerBook | null;
  updatedPartners?: PartnerBookUpdatedPartner[];
  summaryRow?: any;
  buySellDraft?: boolean;
  capitalizedNote?: boolean;
};

type BuildPartnerBookResult = {
  bookOperations: Record<string, string>;
  bookHeaders: Record<string, string>;
  bookComments: Record<string, string>;
  bookDates: Record<string, string>;
  bookColumns: Record<string, string>;
  bookTotals: Record<string, string | number>;
  bookOrder: Record<string, number>;
  bookPartners: any[];
  bookTotalPartners: number;
};

export const buildPartnerBook = ({
  partnerBook,
  updatedPartners,
  summaryRow,
  buySellDraft,
  capitalizedNote,
}: BuildPartnerBookProps): BuildPartnerBookResult | null => {
  if (!partnerBook) {
    return null;
  }

  if (partnerBook && updatedPartners?.length && !capitalizedNote) {
    let keysArray = Object.keys(partnerBook.operations);
    const newBookOperations = {
      ...partnerBook.operations,
      [keysArray.length]: buySellDraft
        ? 'SELL_PARTICIPATION'
        : 'CAPITAL_INCREASE',
      [keysArray.length + 1]: '',
      [keysArray.length + 2]: '',
    };

    keysArray = Object.keys(partnerBook.headers);
    const newBookHeaders = {
      ...partnerBook.headers,
      [keysArray.length]: buySellDraft
        ? 'SELL_PARTICIPATION_DRAFT'
        : 'CAPITAL_DECREASE_DRAFT',
      [keysArray.length + 1]: '',
      [keysArray.length + 2]: '',
    };

    keysArray = Object.keys(partnerBook.comments);
    const newBookComments = {
      ...partnerBook.comments,
      [keysArray.length]: '',
      [keysArray.length + 1]: '',
      [keysArray.length + 2]: '',
    };

    keysArray = Object.keys(partnerBook.dates);
    const newBookDates = {
      ...partnerBook.dates,
      [keysArray.length]: `${new Date().toDateString()}`,
      [keysArray.length + 1]: '',
      [keysArray.length + 2]: '',
    };

    const tempPartners: any = {};
    const newPartnerIds: number[] = [];
    const partnersObj = partnerBook.partners as any;
    Object.keys(partnersObj).forEach((key) => {
      tempPartners[key] = [...partnersObj[key]];
    });
    let partnerIndex = 0;
    updatedPartners.forEach((partner) => {
      if (
        partner?.societyPartnerId &&
        Object.keys(partnerBook.partners).includes(partner?.societyPartnerId)
      ) {
        const index = partner.societyPartnerId;
        keysArray = Object.keys(partnerBook.partners[index as any]);

        // Array for partner values
        const partnerValues = [
          {
            key: keysArray.length,
            value: buySellDraft ? partner.shares.initial : partner.shares.new,
          },
          {
            key: keysArray.length + 1,
            value: buySellDraft ? partner.shares.new : partner.shares.total,
          },
          {
            key: keysArray.length + 2,
            value: buySellDraft ? partner.shares.total : partner.NDTotal,
          },
        ];
        // Only have 4 values if its buySellDraft
        if (buySellDraft) {
          partnerValues.push({
            key: keysArray.length + 3,
            value: partner.NDTotal,
          });
        }

        // The condition its to avoid the sum of const values initials and totals
        partnerValues.forEach((prop) => {
          tempPartners[index][prop.key] =
            buySellDraft &&
            (prop.key === keysArray.length ||
              prop.key === keysArray.length + 2 ||
              prop.key === keysArray.length + 3)
              ? prop.value
              : (tempPartners[index][prop.key] ?? 0) + prop.value;
        });
      } else if (buySellDraft) {
        const partnerId = partner.societyPartnerId;
        const existPartner = tempPartners[partnerId];

        if (!existPartner) {
          tempPartners[partnerId] = [];
        }
        // Put 0 in all previous values of the new partner
        Object.keys(partnerBook.columns).forEach((col, index) => {
          if (index === 0) {
            tempPartners[partnerId][index] = partner.name;
          } else {
            tempPartners[partnerId][index] = 0;
          }
        });
        if (!existPartner) {
          tempPartners[partnerId][keysArray.length] = partner.shares.initial;
          tempPartners[partnerId][keysArray.length + 1] = partner.shares.new;
          tempPartners[partnerId][keysArray.length + 2] = partner.shares.total;
          tempPartners[partnerId][keysArray.length + 3] = partner.NDTotal;
        } else {
          tempPartners[partnerId][keysArray.length + 1] += partner.shares.new;
        }
      } else {
        tempPartners[partnerIndex] = [];
        Object.keys(partnerBook.columns).forEach((col, index) => {
          if (index === 0) {
            tempPartners[partnerIndex][index] = partner.name;
          } else {
            tempPartners[partnerIndex][index] = 0;
          }
        });
        tempPartners[partnerIndex][keysArray.length] = partner.shares.new;
        tempPartners[partnerIndex][keysArray.length + 1] = partner.shares.total;
        tempPartners[partnerIndex][keysArray.length + 2] = partner.NDTotal;
        newPartnerIds.push(partnerIndex);
        partnerIndex += 1;
      }
    });

    const newBookTotalPartners = Object.keys(tempPartners).length;

    const oldPartners: any[] = [];
    const newPartners: any[] = [];
    // Loop to separate old partners and new partners
    Object.entries(tempPartners).forEach(([id, data]) => {
      if (newPartnerIds.includes(+id)) {
        newPartners.push({ id, data });
      } else oldPartners.push({ id, data });
    });
    // Set new partners first then ald partners
    const newBookPartners = [...oldPartners, ...newPartners] as any;

    const newColumns: any = {};
    // Array for column values
    const columnValues = [
      {
        key: keysArray.length,
        value: buySellDraft ? 'Origen' : 'Nuevas',
      },
      {
        key: keysArray.length + 1,
        value: buySellDraft ? 'Post C-V' : 'Total',
      },
      {
        key: keysArray.length + 2,
        value: buySellDraft ? 'Total' : '%',
      },
    ];
    // Only have 4 values if its buySellDraft
    if (buySellDraft) {
      columnValues.push({
        key: keysArray.length + 3,
        value: '%',
      });
    }

    columnValues.forEach((prop) => {
      newColumns[prop.key] = prop.value;
    });

    keysArray = Object.keys(partnerBook.columns);
    const newBookColumns = {
      ...partnerBook.columns,
      ...newColumns,
    };

    keysArray = Object.keys(partnerBook.totals);

    const newTotals: any = {};
    // Array for total values
    const totalValues = [
      {
        key: keysArray.length,
        value: buySellDraft
          ? summaryRow?.totalSharesInitial
          : summaryRow?.totalSharesNew,
      },
      {
        key: keysArray.length + 1,
        value: buySellDraft
          ? summaryRow?.totalSharesNew
          : summaryRow?.totalSharesTotal,
      },
      {
        key: keysArray.length + 2,
        value: buySellDraft ? summaryRow?.totalSharesTotal : 100,
      },
    ];
    // Only have 4 values if its buySellDraft
    if (buySellDraft) {
      totalValues.push({
        key: keysArray.length + 3,
        value: 100,
      });
    }

    totalValues.forEach((prop) => {
      newTotals[prop.key] = prop.value;
    });

    const newBookTotals = {
      ...partnerBook.totals,
      ...newTotals,
    };

    keysArray = Object.keys(partnerBook.order);
    const newBookOrder: any = {
      ...partnerBook.order,
      [keysArray.length]: partnerBook.order[keysArray.length - 1] + 1,
      [keysArray.length + 1]: partnerBook.order[keysArray.length - 1] + 1,
      [keysArray.length + 2]: partnerBook.order[keysArray.length - 1] + 1,
    };

    if (buySellDraft) {
      newBookOrder[keysArray.length + 3] =
        partnerBook.order[keysArray.length - 1] + 1;
    }

    return {
      bookOperations: newBookOperations,
      bookHeaders: newBookHeaders,
      bookComments: newBookComments,
      bookDates: newBookDates,
      bookColumns: newBookColumns,
      bookTotals: newBookTotals,
      bookOrder: newBookOrder,
      bookPartners: newBookPartners,
      bookTotalPartners: newBookTotalPartners,
    };
  }

  if (partnerBook && !updatedPartners?.length) {
    const partners: any[] = [];
    Object.entries(partnerBook.partners).forEach(([id, data]) => {
      partners.push({ id, data });
    });

    return {
      bookDates: partnerBook.dates,
      bookColumns: partnerBook.columns,
      bookHeaders: partnerBook.headers,
      bookComments: partnerBook.comments,
      bookPartners: partners as any,
      bookOperations: partnerBook.operations,
      bookTotals: partnerBook.totals,
      bookOrder: partnerBook.order,
      bookTotalPartners: Object.keys(partnerBook).length,
    };
  }

  return null;
};
